/**
 * Application Setting Definitions API
 * Manages custom field definitions for applications
 */

import api from './api';

export const settingDefinitionsAPI = {
  /**
   * Get all field definitions for an application
   * @param {number} appId - Application ID
   * @param {boolean} activeOnly - Only return active definitions
   * @param {string} sourceType - Optional source_type filter (custom_field, user_field, static_value)
   */
  getAppDefinitions: async (appId, activeOnly = true, sourceType = null) => {
    try {
      const params = {};
      if (activeOnly !== undefined) params.active_only = activeOnly;
      if (sourceType) params.source_type = sourceType;
      
      const response = await api.get(`/applications/${appId}/setting-definitions`, { params });
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch definitions');
      }
      
      return response.data.definitions || [];
    } catch (error) {
      console.error('Failed to get setting definitions:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Get a specific field definition
   * @param {number} appId - Application ID
   * @param {number} defId - Definition ID
   */
  getDefinition: async (appId, defId) => {
    try {
      const response = await api.get(`/applications/${appId}/setting-definitions/${defId}`);
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to fetch definition');
      }
      
      return response.data.definition;
    } catch (error) {
      console.error('Failed to get definition:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Create a new field definition
   * @param {number} appId - Application ID
   * @param {object} definitionData - Definition data
   */
  createDefinition: async (appId, definitionData) => {
    try {
      const response = await api.post(`/applications/${appId}/setting-definitions`, definitionData);
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to create definition');
      }
      
      return response.data.definition;
    } catch (error) {
      console.error('Failed to create definition:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Update a field definition
   * @param {number} appId - Application ID
   * @param {number} defId - Definition ID
   * @param {object} definitionData - Updated definition data
   */
  updateDefinition: async (appId, defId, definitionData) => {
    try {
      const response = await api.put(`/applications/${appId}/setting-definitions/${defId}`, definitionData);
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to update definition');
      }
      
      return response.data.definition;
    } catch (error) {
      console.error('Failed to update definition:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Delete a field definition
   * @param {number} appId - Application ID
   * @param {number} defId - Definition ID
   */
  deleteDefinition: async (appId, defId) => {
    try {
      const response = await api.delete(`/applications/${appId}/setting-definitions/${defId}`);
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to delete definition');
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to delete definition:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Reorder field definitions
   * @param {number} appId - Application ID
   * @param {array} order - Array of definition IDs in desired order
   */
  reorderDefinitions: async (appId, order) => {
    try {
      const response = await api.post(`/applications/${appId}/setting-definitions/reorder`, { order });
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to reorder definitions');
      }
      
      return response.data;
    } catch (error) {
      console.error('Failed to reorder definitions:', error);
      throw error.response?.data || error;
    }
  },

  /**
   * Validate user settings against field definitions
   * @param {number} appId - Application ID
   * @param {object} settings - Settings to validate
   */
  validateSettings: async (appId, settings) => {
    try {
      const response = await api.post(`/applications/${appId}/setting-definitions/validate`, {
        settings
      });
      
      if (response.data.status === 'error') {
        throw new Error(response.data.error || 'Failed to validate settings');
      }
      
      return {
        isValid: response.data.is_valid,
        errors: response.data.errors || []
      };
    } catch (error) {
      console.error('Failed to validate settings:', error);
      throw error.response?.data || error;
    }
  }
};

export default settingDefinitionsAPI;
